import React, { useEffect, useState } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Table,
  Badge,
  Alert,
  Spinner,
  Button,
  Form,
  Modal,
  InputGroup,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  FaPlus,
  FaTrash,
  FaUserCog,
  FaEye,
  FaEyeSlash,
  FaExclamationTriangle,
} from "react-icons/fa";
import { useAuth } from "./auth/AuthContext";

const candidateBases = Array.from(
  new Set(
    [process.env.REACT_APP_API_BASE_URL, "https://api.quvikme.com"].filter(
      Boolean
    )
  )
);

const SubSubAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subSubAdmins, setSubSubAdmins] = useState([]);
  const [subAdmins, setSubAdmins] = useState([]); // Add state for sub-admins
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    societyName: "",
    password: "",
    customId: "",
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [pwdTarget, setPwdTarget] = useState(null);
  const [pwdValue, setPwdValue] = useState("");
  const [pwdSaving, setPwdSaving] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    socId: "",
    societyName: "",
    status: "active",
    password: "",
    customId: "",
  });
  const [showPassword, setShowPassword] = useState(false); // For create modal
  const [showEditPassword, setShowEditPassword] = useState(false); // For edit modal
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const { role, societyId, isAdmin, isSubadmin } = useAuth();

  useEffect(() => {
    fetchSubSubAdmins();
    // Only fetch sub-admins if user is admin (super admin)
    if (isAdmin) {
      fetchSubAdmins();
    }
  }, [isAdmin]);

  const showErrorModalNotification = (message) => {
    setErrorMessage(message);
    setShowErrorModal(true);
  };

  const fetchSubSubAdmins = async () => {
    try {
      setLoading(true);
      let data = null;
      let lastErr = null;
      for (const base of candidateBases) {
        try {
          // Get admin token for authentication
          const adminToken =
            localStorage.getItem("adminToken") ||
            sessionStorage.getItem("adminToken");
          const headers = {
            "Content-Type": "application/json",
          };
          if (adminToken) {
            headers["Authorization"] = `Bearer ${adminToken}`;
          }

          const res = await fetch(`${base}/api/admins/subsubadmins`, {
            headers,
          });
          if (!res.ok) {
            lastErr = new Error(await res.text());
            continue;
          }
          data = await res.json();
          break;
        } catch (e2) {
          lastErr = e2;
          continue;
        }
      }
      if (!data) throw lastErr || new Error("Failed to load sub-sub admins");
      setSubSubAdmins(
        Array.isArray(data.subsubadmins) ? data.subsubadmins : []
      );
      setLoading(false);
    } catch (e) {
      showErrorModalNotification("Failed to load sub-sub admins");
      setLoading(false);
    }
  };

  const fetchSubAdmins = async () => {
    try {
      let data = null;
      let lastErr = null;
      for (const base of candidateBases) {
        try {
          // Get admin token for authentication
          const adminToken =
            localStorage.getItem("adminToken") ||
            sessionStorage.getItem("adminToken");
          const headers = {
            "Content-Type": "application/json",
          };
          if (adminToken) {
            headers["Authorization"] = `Bearer ${adminToken}`;
          }

          const res = await fetch(`${base}/api/admins/subadmins`, { headers });
          if (!res.ok) {
            lastErr = new Error(await res.text());
            continue;
          }
          data = await res.json();
          break;
        } catch (e2) {
          lastErr = e2;
          continue;
        }
      }
      if (!data) throw lastErr || new Error("Failed to load subadmins");
      setSubAdmins(Array.isArray(data.subadmins) ? data.subadmins : []);
    } catch (e) {
      console.error("Failed to load sub-admins:", e);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate required fields
    if (!form.name || !form.email || !form.societyName || !form.password)
      return;

    // If admin, socId is required. If subadmin, socId is auto-assigned
    if (isAdmin && !form.socId) return;

    try {
      let created = null;
      let lastErr = null;
      for (const base of candidateBases) {
        try {
          const payload = { ...form };
          console.log("Original payload:", payload);
          // Only include customId if it's not empty
          if (!form.customId) {
            delete payload.customId;
          }
          // If user is subadmin, don't send socId as it will be auto-assigned by backend
          if (isSubadmin) {
            delete payload.socId;
          }
          // If user is admin but socId is empty, remove it
          if (isAdmin && !form.socId) {
            delete payload.socId;
          }
          // Get admin token for authentication
          const adminToken =
            localStorage.getItem("adminToken") ||
            sessionStorage.getItem("adminToken");
          const headers = {
            "Content-Type": "application/json",
          };
          if (adminToken) {
            headers["Authorization"] = `Bearer ${adminToken}`;
          }
          console.log("Final payload:", payload);
          const res = await fetch(`${base}/api/admins/subsubadmins`, {
            method: "POST",
            headers,
            body: JSON.stringify(payload),
          });
          console.log("Response status:", res.status);
          if (!res.ok) {
            const errorText = await res.text();
            console.log("Error response:", errorText);
            lastErr = new Error(errorText);
            continue;
          }
          created = await res.json();
          console.log("Success response:", created);
          break;
        } catch (e2) {
          console.log("Fetch error:", e2);
          lastErr = e2;
          continue;
        }
      }
      if (!created) throw lastErr || new Error("Create failed");
      setSubSubAdmins((prev) => [created, ...prev]);
      setForm({
        name: "",
        email: "",
        societyName: "",
        password: "",
        customId: "",
      });
      setShowModal(false);
    } catch (e) {
      // Parse the error message to extract just the error text
      let errorMessage = "Failed to create sub-sub admin";
      try {
        const errorObj = JSON.parse(e.message);
        if (errorObj.error) {
          errorMessage = errorObj.error;
        }
      } catch (parseError) {
        // If parsing fails, use the original message
        errorMessage = e.message;
      }
      showErrorModalNotification(errorMessage);
    }
  };

  const handleDelete = async (id) => {
    try {
      let ok = false;
      let lastErr = null;
      for (const base of candidateBases) {
        try {
          // Get admin token for authentication
          const adminToken =
            localStorage.getItem("adminToken") ||
            sessionStorage.getItem("adminToken");
          const headers = {};
          if (adminToken) {
            headers["Authorization"] = `Bearer ${adminToken}`;
          }

          const res = await fetch(`${base}/api/admins/subsubadmins/${id}`, {
            method: "DELETE",
            headers,
          });
          if (!res.ok) {
            lastErr = new Error(await res.text());
            continue;
          }
          ok = true;
          break;
        } catch (e2) {
          lastErr = e2;
          continue;
        }
      }
      if (!ok) throw lastErr || new Error("Delete failed");
      setSubSubAdmins((prev) => prev.filter((sa) => sa.id !== id));
      setDeleteConfirm(null);
    } catch (e) {
      showErrorModalNotification("Failed to delete sub-sub admin");
    }
  };

  const handleSetPassword = async () => {
    if (!pwdTarget || !pwdValue || pwdValue.length < 6) return;
    setPwdSaving(true);
    try {
      let ok = false;
      let lastErr = null;
      for (const base of candidateBases) {
        try {
          // Get admin token for authentication
          const adminToken =
            localStorage.getItem("adminToken") ||
            sessionStorage.getItem("adminToken");
          const headers = {
            "Content-Type": "application/json",
          };
          if (adminToken) {
            headers["Authorization"] = `Bearer ${adminToken}`;
          }

          const res = await fetch(
            `${base}/api/admins/subsubadmins/${pwdTarget.id}/password`,
            {
              method: "PUT",
              headers,
              body: JSON.stringify({ password: pwdValue }),
            }
          );
          if (!res.ok) {
            lastErr = new Error(await res.text());
            continue;
          }
          ok = true;
          break;
        } catch (e2) {
          lastErr = e2;
          continue;
        }
      }
      if (!ok) throw lastErr || new Error("Update failed");
      setPwdTarget(null);
      setPwdValue("");
    } catch (e) {
      showErrorModalNotification("Failed to update password");
    } finally {
      setPwdSaving(false);
    }
  };

  const handleViewAs = (sa) => {
    try {
      sessionStorage.setItem("QV_ROLE", "subsubadmin");
      if (sa.societyName)
        sessionStorage.setItem("QV_SOCIETY_ID", String(sa.societyName));
      if (sa.name)
        sessionStorage.setItem("QV_SUBSUBADMIN_NAME", String(sa.name));
      // Redirect to sub-sub admin dashboard instead of opening in new tab
      window.location.href = "/#/subsubadmin/dashboard";
    } catch {}
  };

  const openEdit = (sa) => {
    setEditTarget(sa);
    setEditForm({
      name: sa.name || "",
      email: sa.email || "",
      socId: sa.socId || "",
      societyName: sa.societyName || "",
      status: (sa.status || "active").toString().toLowerCase(),
      password: "",
      customId: sa.customId || "",
    });
  };

  const saveEdit = async () => {
    if (!editTarget) return;
    const payload = {
      name: editForm.name,
      email: editForm.email,
      societyName: editForm.societyName,
      status: editForm.status,
    };
    // Only include customId if it's not empty
    if (editForm.customId) {
      payload.customId = editForm.customId;
    }
    // Only include socId if user is admin
    if (isAdmin) {
      payload.socId = editForm.socId;
    }
    if (editForm.password && editForm.password.length >= 6)
      payload.password = editForm.password;
    try {
      let updated = null;
      let lastErr = null;
      for (const base of candidateBases) {
        try {
          // Get admin token for authentication
          const adminToken =
            localStorage.getItem("adminToken") ||
            sessionStorage.getItem("adminToken");
          const headers = {
            "Content-Type": "application/json",
          };
          if (adminToken) {
            headers["Authorization"] = `Bearer ${adminToken}`;
          }

          const res = await fetch(
            `${base}/api/admins/subsubadmins/${editTarget.id}`,
            {
              method: "PUT",
              headers,
              body: JSON.stringify(payload),
            }
          );
          if (!res.ok) {
            lastErr = new Error(await res.text());
            continue;
          }
          updated = await res.json();
          break;
        } catch (e2) {
          lastErr = e2;
          continue;
        }
      }
      if (!updated) throw lastErr || new Error("Update failed");
      setSubSubAdmins((prev) =>
        prev.map((sa) => (sa.id === updated.id ? updated : sa))
      );
      setEditTarget(null);
    } catch (e) {
      // Parse the error message to extract just the error text
      let errorMessage = "Failed to update sub-sub admin";
      try {
        const errorObj = JSON.parse(e.message);
        if (errorObj.error) {
          errorMessage = errorObj.error;
        }
      } catch (parseError) {
        // If parsing fails, use the original message
        errorMessage = e.message;
      }
      showErrorModalNotification(errorMessage);
    }
  };

  // Function to get sub-admin name by ID
  const getSubAdminName = (socId) => {
    const subAdmin = subAdmins.find((sa) => sa.id === socId);
    return subAdmin ? subAdmin.name : "Unknown Sub-Admin";
  };

  const statusBadge = (status) => (
    <Badge
      bg={String(status).toLowerCase() === "active" ? "success" : "secondary"}
      className="p-2"
    >
      {String(status || "active").toUpperCase()}
    </Badge>
  );

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "40vh" }}
      >
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <Container fluid className="px-3 px-md-4 py-3">
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2 mb-4">
        <h2 className="mb-0 fw-bold">Sub-Sub Admins</h2>
        <Button variant="primary" size="sm" onClick={() => setShowModal(true)}>
          <FaPlus className="me-2" /> Add Sub-Sub Admin
        </Button>
      </div>

      <Card className="shadow-sm border-0">
        <Card.Header className="bg-light border-0 py-3">
          <h5 className="mb-0 fw-bold">Manage Sub-Sub Admins</h5>
        </Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover className="align-middle text-nowrap mb-0">
            <thead>
              <tr>
                <th>Custom ID</th>
                <th>Name</th>
                <th>Email</th>
                {isAdmin && <th>Parent Sub-Admin</th>}
                <th>Society Name</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {subSubAdmins.map((sa) => (
                <tr key={sa.id}>
                  <td>{sa.customId || "—"}</td>
                  <td>{sa.name}</td>
                  <td>{sa.email}</td>
                  {isAdmin && <td>{getSubAdminName(sa.socId)}</td>}
                  <td>{sa.societyName}</td>
                  <td>{statusBadge(sa.status)}</td>
                  <td>
                    <div className="d-flex gap-2">
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => openEdit(sa)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => setDeleteConfirm(sa)}
                      >
                        <FaTrash />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Create Sub-Sub Admin Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Form onSubmit={handleCreate}>
          <Modal.Header closeButton>
            <Modal.Title>Create Sub-Sub Admin</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Custom ID (Optional)</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter custom ID (e.g., 1, 2, 3)"
                value={form.customId}
                onChange={(e) => setForm({ ...form, customId: e.target.value })}
              />
            </Form.Group>
            {isAdmin && (
              <Form.Group className="mb-3">
                <Form.Label>Parent Sub-Admin</Form.Label>
                <Form.Select
                  value={form.socId}
                  onChange={(e) => setForm({ ...form, socId: e.target.value })}
                  required
                >
                  <option value="">Select a sub-admin</option>
                  {subAdmins.map((subAdmin) => (
                    <option key={subAdmin.id} value={subAdmin.id}>
                      {subAdmin.name} ({subAdmin.areaName})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Society Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter society name"
                value={form.societyName}
                onChange={(e) =>
                  setForm({ ...form, societyName: e.target.value })
                }
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter password"
                  value={form.password}
                  onChange={(e) =>
                    setForm({ ...form, password: e.target.value })
                  }
                  required
                  minLength="6"
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </Button>
              </InputGroup>
              <Form.Text className="text-muted">
                Password must be at least 6 characters long.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Create
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit Sub-Sub Admin Modal */}
      {editTarget && (
        <Modal show={!!editTarget} onHide={() => setEditTarget(null)}>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              saveEdit();
            }}
          >
            <Modal.Header closeButton>
              <Modal.Title>Edit Sub-Sub Admin</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter name"
                  value={editForm.name}
                  onChange={(e) =>
                    setEditForm({ ...editForm, name: e.target.value })
                  }
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter email"
                  value={editForm.email}
                  onChange={(e) =>
                    setEditForm({ ...editForm, email: e.target.value })
                  }
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Custom ID (Optional)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter custom ID (e.g., 1, 2, 3)"
                  value={editForm.customId}
                  onChange={(e) =>
                    setEditForm({ ...editForm, customId: e.target.value })
                  }
                />
              </Form.Group>
              {isAdmin && (
                <Form.Group className="mb-3">
                  <Form.Label>Parent Sub-Admin</Form.Label>
                  <Form.Select
                    value={editForm.socId}
                    onChange={(e) =>
                      setEditForm({ ...editForm, socId: e.target.value })
                    }
                    required
                  >
                    <option value="">Select a sub-admin</option>
                    {subAdmins.map((subAdmin) => (
                      <option key={subAdmin.id} value={subAdmin.id}>
                        {subAdmin.name} ({subAdmin.areaName})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}
              <Form.Group className="mb-3">
                <Form.Label>Society Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter society name"
                  value={editForm.societyName}
                  onChange={(e) =>
                    setEditForm({ ...editForm, societyName: e.target.value })
                  }
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={editForm.status}
                  onChange={(e) =>
                    setEditForm({ ...editForm, status: e.target.value })
                  }
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Password (leave blank to keep current)</Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showEditPassword ? "text" : "password"}
                    placeholder="Enter new password"
                    value={editForm.password}
                    onChange={(e) =>
                      setEditForm({ ...editForm, password: e.target.value })
                    }
                    minLength="6"
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => setShowEditPassword(!showEditPassword)}
                  >
                    {showEditPassword ? <FaEyeSlash /> : <FaEye />}
                  </Button>
                </InputGroup>
                <Form.Text className="text-muted">
                  Password must be at least 6 characters long.
                </Form.Text>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setEditTarget(null)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Save Changes
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={!!deleteConfirm} onHide={() => setDeleteConfirm(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete sub-sub admin{" "}
          <strong>{deleteConfirm?.name}</strong>? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => handleDelete(deleteConfirm?.id)}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Password Change Modal */}
      <Modal
        show={!!pwdTarget}
        onHide={() => {
          setPwdTarget(null);
          setPwdValue("");
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Change Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>New Password for {pwdTarget?.name}</Form.Label>
            <Form.Control
              type="password"
              placeholder="Enter new password"
              value={pwdValue}
              onChange={(e) => setPwdValue(e.target.value)}
              minLength="6"
              disabled={pwdSaving}
            />
            <Form.Text className="text-muted">
              Password must be at least 6 characters long.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setPwdTarget(null);
              setPwdValue("");
            }}
            disabled={pwdSaving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSetPassword}
            disabled={pwdSaving || !pwdValue || pwdValue.length < 6}
          >
            {pwdSaving ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Error Modal */}
      <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)}>
        <Modal.Header closeButton className="bg-warning">
          <Modal.Title className="d-flex align-items-center">
            <FaExclamationTriangle className="me-2" />
            Error
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{errorMessage}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowErrorModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default SubSubAdmin;
